import { useState } from "react";

export default function LetsConnectButton({ onClick, variant = "default", className = "" }) {
  const [isRippling, setIsRippling] = useState(false);
  const [ripplePosition, setRipplePosition] = useState({ x: 0, y: 0 });

  const handleClick = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    setRipplePosition({ x, y });
    setIsRippling(true);

    setTimeout(() => setIsRippling(false), 600);

    if (onClick) onClick();
  };

  const isNavbar = variant === "navbar";

  return (
    <>
      <button
        onClick={handleClick}
        className={`relative overflow-hidden transition-all duration-300 hover:scale-105 active:scale-95 group ${className}`}
        style={{
          fontFamily: "'Orbitron', monospace",
          fontSize: isNavbar ? "10px" : "12px",
          fontWeight: 600,
          letterSpacing: isNavbar ? "2px" : "3px",
          textTransform: "uppercase",
          padding: isNavbar ? "10px 20px" : "16px 32px",
          background: "linear-gradient(135deg, rgba(61, 0, 102, 0.9) 0%, rgba(13, 0, 16, 0.95) 50%, rgba(155, 0, 255, 0.7) 100%)",
          border: "1px solid rgba(155, 0, 255, 0.5)",
          borderRadius: "4px",
          color: "#f0e0ff",
          boxShadow: "0 0 20px rgba(155, 0, 255, 0.3), inset 0 1px 0 rgba(155, 0, 255, 0.2)",
        }}
        data-testid="lets-connect-btn"
      >
        {/* Pulsating glow ring */}
        <div
          className="absolute inset-0 rounded pointer-events-none"
          style={{
            border: "1px solid rgba(155, 0, 255, 0.6)",
            animation: "pulseGlow 2s ease-in-out infinite",
          }}
        />

        {/* Outer pulsating ring */}
        <div
          className="absolute -inset-1 rounded pointer-events-none opacity-50"
          style={{
            border: "1px solid rgba(155, 0, 255, 0.3)",
            animation: "pulseGlow 2s ease-in-out infinite 0.5s",
          }}
        />

        {/* Content */}
        <span className="relative z-10 flex items-center gap-2">
          <svg
            width={isNavbar ? "12" : "14"}
            height={isNavbar ? "12" : "14"}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="transition-transform duration-300 group-hover:rotate-12"
          >
            <line x1="22" y1="2" x2="11" y2="13" />
            <polygon points="22 2 15 22 11 13 2 9 22 2" />
          </svg>
          Let's Connect
        </span>

        {/* Ripple effect */}
        {isRippling && (
          <span
            className="absolute rounded-full animate-ping pointer-events-none"
            style={{
              left: ripplePosition.x - 10,
              top: ripplePosition.y - 10,
              width: 20,
              height: 20,
              background: "radial-gradient(circle, rgba(155, 0, 255, 0.8), transparent 70%)",
              animation: "rippleBurst 0.6s ease-out forwards",
            }}
          />
        )}

        {/* Hover shine effect */}
        <div
          className="absolute top-0 -left-full w-1/2 h-full skew-x-12 transition-all duration-500 group-hover:left-full pointer-events-none"
          style={{
            background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.15), transparent)",
          }}
        />

        {/* Corner accents */}
        <div
          className="absolute top-0 left-0 w-2 h-2 pointer-events-none"
          style={{
            borderTop: "1px solid #00d4ff",
            borderLeft: "1px solid #00d4ff",
            boxShadow: "-1px -1px 5px rgba(0, 212, 255, 0.5)",
          }}
        />
        <div
          className="absolute bottom-0 right-0 w-2 h-2 pointer-events-none"
          style={{
            borderBottom: "1px solid #00d4ff",
            borderRight: "1px solid #00d4ff",
            boxShadow: "1px 1px 5px rgba(0, 212, 255, 0.5)",
          }}
        />
      </button>

      <style>{`
        @keyframes pulseGlow {
          0%, 100% {
            box-shadow: 0 0 5px rgba(155, 0, 255, 0.3), inset 0 0 5px rgba(155, 0, 255, 0.1);
            transform: scale(1);
          }
          50% {
            box-shadow: 0 0 20px rgba(155, 0, 255, 0.6), inset 0 0 10px rgba(155, 0, 255, 0.2);
            transform: scale(1.02);
          }
        }

        @keyframes rippleBurst {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          100% {
            transform: scale(20);
            opacity: 0;
          }
        }
      `}</style>
    </>
  );
}
